import React from 'react';
import { Button, Table, Badge, Tabs, Tab } from 'react-bootstrap';
import { Calendar, PlusCircle, Upload, Download, FileText, BookCopy, AlertTriangle, Info, Share, Trash2, Plus } from 'lucide-react';
import FileDropZone from '../../SharedComponents/FileDropZone';
import AccountPlanManager from '../AccountPlanManager';
import PayrollCompanySettings from '../PayrollCompanySettings';

// Bookkeeping section — fiscal years, SIE import/export, bookkeeping templates
// (with nested private/shared tabs), the account plan manager and payroll/
// semester company settings. Data is lazy-loaded in the CompanySettings shell
// on first visit and passed in as props; modals live in the shell.
const BookkeepingSection = ({
  companyId,
  companyType,
  fiscalYears,
  templates,
  files,
  isLoading,
  activeTemplateTab,
  setActiveTemplateTab,
  setFiles,
  setShowCreateFiscalYearModal,
  navigateToFiscalYearDetails,
  handleFiscalYearAction,
  handleUpload,
  handleOpenExportModal,
  navigateToTemplateEditor,
  handleSubmitForSharing,
  handleDeleteTemplate,
}) => (
  <>
    {/* Fiscal Years Section */}
    <div className="modern-card" id="rakenskapsar">
      <div className="modern-card-header">
        <div>
          <h2 className="section-title">
            <Calendar size={24} />
            Räkenskapsår
          </h2>
          <p className="section-subtitle">
            Hantera och konfigurera räkenskapsår för ditt företag
          </p>
        </div>
        <div className="action-buttons">
          <Button
            variant="primary"
            onClick={() => setShowCreateFiscalYearModal(true)}
            className="d-flex align-items-center gap-2"
          >
            <PlusCircle size={16} />
            Skapa nytt räkenskapsår
          </Button>
        </div>
      </div>

      <div className="modern-card-body">
        {fiscalYears.length > 0 ? (
          <div className="modern-table-container">
            <Table responsive hover className="modern-table">
              <thead>
                <tr>
                  <th>Räkenskapsår</th>
                  <th>Period</th>
                  <th>Status</th>
                  <th>Redovisningsmetod</th>
                  <th>Momsperiod</th>
                  <th>Åtgärder</th>
                </tr>
              </thead>
              <tbody>
                {fiscalYears.map((year) => (
                  <tr
                    key={year.fiscal_year_id}
                    onClick={navigateToFiscalYearDetails(year.fiscal_year_id)}
                    className="clickable-row"
                  >
                    <td>
                      <div className="table-cell-content">
                        <div className="fiscal-year-badge">
                          {year.year}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="table-cell-content">
                        <div className="date-range">
                          <small className="text-muted">
                            {new Date(year.start_date).toLocaleDateString('sv-SE')} -
                            {new Date(year.end_date).toLocaleDateString('sv-SE')}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="table-cell-content">
                        <Badge
                          variant={year.state === 'open' ? 'success' : 'secondary'}
                          className="status-badge"
                        >
                          {year.state === 'open' ? 'Öppen' : 'Stängd'}
                        </Badge>
                      </div>
                    </td>
                    <td>
                      <div className="table-cell-content">
                        <span className="method-text">
                          {year.accounting_method === 'accrual' ? 'Faktureringsmetoden' : 'Kontantmetoden'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="table-cell-content">
                        <span className="vat-period-text">
                          {year.vat_period === 'monthly' ? 'Månadsvis' :
                            year.vat_period === 'quarterly' ? 'Kvartalsvis' : 'Årsvis'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="table-actions">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFiscalYearAction(year.fiscal_year_id, year.state === 'open' ? 'close' : 'open');
                          }}
                          variant={year.state === 'open' ? 'outline-warning' : 'outline-success'}
                          size="sm"
                          className="action-button"
                        >
                          {year.state === 'open' ? 'Stäng år' : 'Öppna år'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="empty-state">
            <Calendar size={48} className="empty-icon" />
            <h4>Inga räkenskapsår skapade än</h4>
            <p>Skapa ditt första räkenskapsår för att komma igång med bokföringen. Ett räkenskapsårs kan också skapas genom att importera bokföringen från ett annat system.</p>
            <Button
              variant="primary"
              onClick={() => setShowCreateFiscalYearModal(true)}
              className="mt-3"
            >
              <PlusCircle className="me-2" size={16} />
              Skapa räkenskapsår
            </Button>
          </div>
        )}
      </div>
    </div>

    {/* Import/Export Section */}
    <div className="modern-card" id="import-export">
      <div className="modern-card-header">
        <div>
          <h2 className="section-title">
            <Upload size={24} />
            Import & Export
          </h2>
          <p className="section-subtitle">
            Importera och exportera bokföringsdata från/till andra system
          </p>
        </div>
      </div>

      <div className="modern-card-body">
        <div className="import-export-grid">
          {/* Import Section */}
          <div className="import-export-card import-card">
            <div className="card-icon import-icon">
              <Download size={32} />
            </div>
            <div className="card-content">
              <h3 className="card-title">Importera data</h3>
              <p className="card-description">
                Importera bokföringsdata från andra system via SIE-filer
              </p>

              <div className="file-drop-container">
                <FileDropZone
                  setFiles={setFiles}
                  fileTypes='.se'
                  minHeight='120px'
                  subText='Dra och släpp .se fil här eller klicka för att välja'
                  acceptedFileTypes='.se'
                  multiple='false'
                />
              </div>

              <Button
                variant="primary"
                onClick={handleUpload}
                disabled={isLoading || files.length === 0}
                className="w-100 import-button"
              >
                <Upload className="me-2" size={16} />
                {isLoading ? 'Kontrollerar...' : 'Kontrollera och importera'}
                {isLoading && (
                  <span className="spinner-border spinner-border-sm ms-2" />
                )}
              </Button>
            </div>
          </div>

          {/* Export Section */}
          <div className="import-export-card export-card">
            <div className="card-icon export-icon">
              <Upload size={32} />
            </div>
            <div className="card-content">
              <h3 className="card-title">Exportera data</h3>
              <p className="card-description">
                Exportera bokföringsdata till andra system i olika format
              </p>

              <div className="export-options">
                <div className="export-option">
                  <div className="export-option-info">
                    <div className="export-format">
                      <FileText size={20} />
                      <div>
                        <h6>SIE-format</h6>
                        <small>Standard export format för svenska system</small>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleOpenExportModal}
                    disabled={isLoading}
                    className="export-button"
                  >
                    <Download size={14} />
                    Exportera
                  </Button>
                </div>

                <div className="export-option disabled">
                  <div className="export-option-info">
                    <div className="export-format">
                      <FileText size={20} />
                      <div>
                        <h6>CSV-format</h6>
                        <small>Comma-separated values för Excel</small>
                      </div>
                    </div>
                  </div>
                  <Button variant="outline-secondary" size="sm" disabled>
                    <span className="badge bg-warning text-dark me-2">Snart</span>
                    Exportera
                  </Button>
                </div>

                <div className="export-option disabled">
                  <div className="export-option-info">
                    <div className="export-format">
                      <FileText size={20} />
                      <div>
                        <h6>Excel-format</h6>
                        <small>Excel arbetsbok (.xlsx)</small>
                      </div>
                    </div>
                  </div>
                  <Button variant="outline-secondary" size="sm" disabled>
                    <span className="badge bg-warning text-dark me-2">Snart</span>
                    Exportera
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Templates Section */}
    <div className="modern-card" id="mallar">
      <div className="modern-card-header">
        <div>
          <h2 className="section-title">
            <BookCopy size={24} />
            Bokföringsmallar
          </h2>
          <p className="section-subtitle">
            Skapa och hantera mallar för återkommande bokföringstransaktioner
          </p>
        </div>
        <div className="action-buttons">
          <Button
            variant="primary"
            onClick={() => navigateToTemplateEditor()}
            className="d-flex align-items-center gap-2"
          >
            <PlusCircle size={16} />
            Skapa ny mall
          </Button>
        </div>
      </div>

      <div className="modern-card-body">
        <Tabs
          activeKey={activeTemplateTab}
          onSelect={(k) => setActiveTemplateTab(k)}
          className="mb-4"
        >
          {/* Private Templates Tab */}
          <Tab
            eventKey="private"
            title={`Mina mallar (${templates.filter((t) => t.source === 'tenant').length})`}
          >
            {templates.filter(t => t.source === 'tenant').length > 0 ? (
              <div className="templates-grid">
                {templates
                  .filter(t => t.source === 'tenant')
                  .map((template) => (
                    <div key={template.template_id} className="template-card">
                      <div className="template-icon">
                        <BookCopy size={24} />
                      </div>
                      <div className="template-content">
                        <h4 className="template-name">
                          {template.template_name}
                          {/* Status Badges - Only for Pending/Rejected */}
                          {template.approval_status === 'pending_approval' && (
                            <Badge bg="warning" className="ms-2">Väntar på godkännande</Badge>
                          )}
                          {template.approval_status === 'rejected' && (
                            <Badge bg="danger" className="ms-2">Nekad</Badge>
                          )}
                        </h4>
                        <p className="template-description">{template.description}</p>

                        {/* Rejection Reason Alert */}
                        {template.rejection_reason && (
                          <div className="alert alert-danger mt-2 mb-0" role="alert">
                            <AlertTriangle size={14} className="me-1" />
                            <strong>Anledning:</strong> {template.rejection_reason}
                          </div>
                        )}
                      </div>
                      <div className="template-actions">
                        {/* Visa detaljer — the single edit entry point for
                            every template (private or system). The editor
                            routes save to PUT for tenant rows and POST-as-
                            private for system/shared rows; users see one
                            consistent path. */}
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => navigateToTemplateEditor(template.template_id)}
                          className="template-edit-btn me-2"
                        >
                          <Info size={14} />
                          Visa detaljer
                        </Button>

                        {/* Submit for Sharing Button - Only for tenant templates */}
                        {!template.shared_template_id && (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-2"
                            onClick={() => handleSubmitForSharing(template.template_id)}
                          >
                            <Share size={14} />
                            Dela
                          </Button>
                        )}

                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteTemplate(
                            template.template_id, template.template_name)}
                        >
                          <Trash2 size={14} />
                          Ta bort
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="empty-state">
                <BookCopy size={48} className="empty-icon" />
                <h4>Inga privata mallar än</h4>
                <p>Skapa mallar för att effektivisera återkommande bokföringstransaktioner</p>
                <Button
                  variant="primary"
                  onClick={() => navigateToTemplateEditor()}
                  className="mt-3"
                >
                  <Plus className="me-2" size={16} />
                  Skapa första mallen
                </Button>
              </div>
            )}
          </Tab>

          {/* Shared/Global Templates Tab */}
          <Tab
            eventKey="shared"
            title={`Mallar (${templates.filter((t) => t.source === 'shared' || t.source === 'pending').length})`}
          >
            {templates.filter(t => t.source === 'shared' || t.source === 'pending').length > 0 ? (
              <div className="templates-grid">
                {templates
                  .filter(t => t.source === 'shared' || t.source === 'pending')
                  .map((template) => (
                    <div key={template.template_id} className="template-card">
                      <div className="template-icon">
                        <BookCopy size={24} />
                      </div>
                      <div className="template-content">
                        <h4 className="template-name">
                          {template.template_name}
                        </h4>
                        <p className="template-description">{template.description}</p>
                      </div>
                      <div className="template-actions">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => navigateToTemplateEditor(template.template_id)}
                        >
                          <Info size={14} />
                          Visa detaljer
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="empty-state">
                <BookCopy size={48} className="empty-icon" />
                <h4>Inga delade mallar tillgängliga</h4>
                <p>Delade mallar från andra företag kommer visas här</p>
              </div>
            )}
          </Tab>
        </Tabs>
      </div>
    </div>

    {/* Account Plan Section */}
    <div id="kontoplan">
      <AccountPlanManager companyId={companyId} companyType={companyType} />
    </div>

    {/* Payroll / Semester company settings */}
    <div id="lon">
      <PayrollCompanySettings />
    </div>
  </>
);

export default BookkeepingSection;
